import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import { Link } from '@/components/atoms/Link';
import FormInput from '@/components/molecules/FormInput';
import LocationAutoCompleteInput from '@/components/molecules/LocationAutoCompleteInput/LocationAutoCompleteInput';
import { useZipCoords } from '@/hooks/useZipCoords';
import { browserLocation } from '@/lib/browserLocation';
import {
  CombinedJDIncentive,
  JDPowerIncentive,
} from '@/lib/schema/incentives/types';
import { validateEmail } from '@/lib/validators';
import { validatePhone } from '@/lib/validators/phone';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import {
  Map,
  useApiIsLoaded as useIsGoogleMapsLoaded,
} from '@vis.gl/react-google-maps';
import clsx from 'clsx';
import React, {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { IncentiveKind } from './Benefits';

interface OverlayProps {
  onClose: () => void;
}

interface OverlayWrapperProps extends OverlayProps {
  title: string;
  subtitle?: string;
}

export const OverlayWrapper: React.FC<
  PropsWithChildren<OverlayWrapperProps>
> = ({ onClose, title, subtitle, children }) => {
  return (
    <div className="flex flex-col gap-l bg-white px-s">
      <div className="flex flex-col gap-xs px-s pt-l ml:px-l">
        <div className="flex justify-between">
          <div className="text-body1Medium text-neutral-900">{title}</div>
          <div role="button" onClick={onClose}>
            <XMarkIcon className="size-xl fill-neutral-900" />
          </div>
        </div>
        {subtitle != null ? (
          <div className="text-body1Regular text-neutral-900">{subtitle}</div>
        ) : null}
      </div>
      <div className="h-px w-full bg-neutral-300" />
      {children}
    </div>
  );
};

interface IncentiveOverlayProps extends OverlayProps {
  incentive: JDPowerIncentive | CombinedJDIncentive;
  kind: IncentiveKind;
}

export const IncentiveOverlay: React.FC<IncentiveOverlayProps> = ({
  incentive,
  kind,
  onClose,
}) => {
  let message = `This benefit enhances your ownership experience without involving a direct cash incentive.`;
  if (kind === 'now') {
    message = `This credit is claimed as a discount on the sale price when purchasing the vehicle`;
  } else if (kind === 'later') {
    message = `This credit is claimed after purchase, typically through a tax return or rebate application.`;
  }

  let descriptions: ReactElement[] = [];
  if ('incentives' in incentive) {
    descriptions = incentive.incentives.map((incent, i) => (
      <React.Fragment key={i}>
        <div className="pb-s text-body1Medium text-neutral-800">
          {incent.name}
        </div>
        <div className="whitespace-pre-line text-body2Regular text-neutral-800">
          {incent.description}
        </div>
        {incent.details_url ? (
          <Link
            className="text-body2Regular text-blue-medium"
            href={incent.details_url}
            target="_blank"
          >
            Learn more
          </Link>
        ) : null}
      </React.Fragment>
    ));
  } else {
    descriptions = [
      <>
        <div className="whitespace-pre-line text-body2Regular text-neutral-800">
          {incentive.description}
        </div>
        {incentive.details_url ? (
          <Link
            className="text-body2Regular text-blue-medium"
            href={incentive.details_url}
            target="_blank"
          >
            Learn more
          </Link>
        ) : null}
      </>,
    ];
  }
  return (
    <OverlayWrapper
      onClose={onClose}
      title={incentive.name}
      subtitle={
        incentive.max_amount != null
          ? `Up to $${incentive.max_amount.toLocaleString()}`
          : undefined
      }
    >
      <div className="flex flex-col gap-l overflow-y-auto px-s pb-s ml:px-l">
        <div className="text-body2Medium text-neutral-800">{message}</div>
        {descriptions.length > 0 &&
          descriptions.map((desc, i) => <div key={i}>{desc}</div>)}
      </div>
    </OverlayWrapper>
  );
};

const gasDisclaimer = ({ miles, price, rate, bodyType }: GasDisclaimerInfo) =>
  `Efficiency assessments are guided by EPA standards, factoring in 45% highway and 55% city driving for ${miles.toLocaleString()} annual miles. Costs are based on national averages: $${price.gas.toFixed(2)} per gallon of gasoline and $${price.ev.toPrecision(2)} per kWh for electricity. Actual savings may vary due to market changes and driving conditions. Consumption ratings are provided for both electric vehicles (in kWh/100mi) and gasoline vehicles (in MPG).
  
  Average gasoline ${bodyType} consumption rating = ${rate.gas} MPG.
  Average EV ${bodyType} consumption rating = ${rate.ev} kWh/100mi.`;

interface GasDisclaimerInfo {
  miles: number;
  price: { gas: number; ev: number };
  rate: { gas: number; ev: number };
  bodyType: string;
}

interface DisclaimerOverlayProps extends OverlayProps {
  gasDisclaimerInfo: GasDisclaimerInfo;
}

export const DisclaimerOverlay: React.FC<DisclaimerOverlayProps> = ({
  onClose,
  gasDisclaimerInfo,
}) => {
  return (
    <OverlayWrapper onClose={onClose} title="EV Savings calculations">
      <div className="flex flex-col gap-l overflow-y-auto px-s pb-s ml:px-l">
        <div className="text-body2Regular text-neutral-800">
          {`*EV Savings and benefits are calculated by an independent third-party provider and not affiliated with any car brand or our dealership—estimations and displayed for informational purposes only. Please verify your eligibility for incentive programs and individual vehicle running costs with the dealer before purchasing a vehicle.`}
        </div>
        <div>
          <div className="text-body1Medium text-neutral-800">Gas savings</div>
          <div className="whitespace-pre-line text-body2Regular text-neutral-800">
            {gasDisclaimer(gasDisclaimerInfo)}
          </div>
        </div>
      </div>
    </OverlayWrapper>
  );
};

const defaultMapSettings = {
  center: {
    lat: 40.7128,
    lng: -74.006,
  },
  zoom: 12,
};

interface LocationOverlayProps extends OverlayProps {
  defaultZip: string;
  onUpdate: (zip: string, address: string | undefined) => void;
}

// TODO: use this updated zipcode logic in location pop out and search filter controls
export const LocationOverlay: React.FC<LocationOverlayProps> = ({
  onClose,
  defaultZip,
  onUpdate,
}) => {
  const [zip, setZip] = useState(defaultZip);
  const [address, setAddress] = useState<string>();
  const googleMapsLoaded = useIsGoogleMapsLoaded();
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [loadingGeolocation, setLoadingGeoLocation] = useState(false);
  const {
    coords,
    isError: zipCoordError,
    isLoading: zipCoordLoading,
  } = useZipCoords(zip);

  const isLoading = zipCoordLoading || loadingGeolocation;
  const isValid = !!coords && !zipCoordError;
  const showErrMessage = !isLoading && !isValid;
  const allowSubmit = !isLoading && isValid;

  useEffect(() => {
    if (coords) {
      setMapCenter(coords);
    }
  }, [coords]);

  const useGeolocation = () => {
    setLoadingGeoLocation(true);
    browserLocation()
      .then(({ latLng, zip: _zip }) => {
        if (!_zip) {
          throw new Error();
        }

        setMapCenter(latLng);
        setLoadingGeoLocation(false);
        setZip(_zip);
      })
      .catch(() => {
        setLoadingGeoLocation(false);
      });
  };

  useEffect(() => {
    if (loadingGeolocation) document.body.classList.add('!cursor-progress');
    else document.body.classList.remove('!cursor-progress');
  }, [loadingGeolocation]);

  const googleMap = (
    <Map
      center={mapCenter}
      defaultCenter={defaultMapSettings.center}
      defaultZoom={defaultMapSettings.zoom}
      disableDefaultUI
      styles={[
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ]}
      draggableCursor="default"
    />
  );

  return (
    <OverlayWrapper onClose={onClose} title="Location">
      <div className="px-s ml:flex ml:justify-between ml:px-l">
        <div className="flex flex-col gap-l ml:grow">
          <div
            className={clsx('flex flex-col', {
              'gap-l': showErrMessage,
              'gap-xl': !showErrMessage,
            })}
          >
            <div className="flex flex-col gap-s">
              <div className="text-body2Regular text-neutral-800">
                Enter your city or Zip Code to view EV Savings in your area.
              </div>
              <div className="ml:w-[250px]">
                <LocationAutoCompleteInput
                  zipCode={zip}
                  setZip={setZip}
                  setAddress={setAddress}
                  useGeolocation={useGeolocation}
                  message="Please enter a valid US zipcode"
                  invalid={showErrMessage}
                  isGooglePlacesLibraryLoaded={googleMapsLoaded}
                  label=""
                />
              </div>
            </div>
            <div className="h-[200px] w-full overflow-hidden rounded-xsmall s:h-[240px] ml:hidden">
              {googleMap}
            </div>
          </div>
          <div className="ml:w-[250px]">
            <Button
              aria-label="Update location"
              variant={ButtonVariants.Tertiary}
              // TODO: simplify address to lowest gov't level (i.e. city)
              onClick={() => {
                onUpdate(zip, address);
                onClose();
              }}
              disabled={!allowSubmit}
            >
              Update location
            </Button>
          </div>
        </div>
        <div className="hidden h-[300px] w-[520px] overflow-hidden rounded-xsmall ml:block">
          {googleMap}
        </div>
      </div>
    </OverlayWrapper>
  );
};

interface CTAOverlayProps extends OverlayProps {
  name: string;
  onChangeName: (val: string) => void;
  email: string;
  onChangeEmail: (val: string) => void;
  phone: string;
  onChangePhone: (val: string) => void;
  onSubmit: () => void;
  success: boolean;
  error: boolean;
  loading: boolean;
}

export const CTAOverlay: React.FC<CTAOverlayProps> = ({
  name,
  onChangeName,
  email,
  onChangeEmail,
  phone,
  onChangePhone,
  onSubmit,
  onClose,
  success,
  error,
  loading,
}) => {
  const emailValid = validateEmail(email);
  const phoneValid = validatePhone(phone);
  const formValid = !!name && emailValid && (!phone || phoneValid);

  return (
    <OverlayWrapper onClose={onClose} title="Confirm EV Savings">
      {success || error || loading ? (
        <div className="flex min-h-[300px] flex-col items-center justify-center gap-l px-s py-xl">
          {success && (
            <CheckCircleIcon className="w-[48px] stroke-green-medium" />
          )}
          {error && <XCircleIcon className="w-[48px] stroke-red-medium" />}
          {loading && (
            <ArrowPathIcon className="w-[48px] animate-spin stroke-neutral-800" />
          )}
          <div className="flex flex-col items-center gap-s text-center">
            {success && (
              <>
                <div className="text-h4Medium text-green-medium">Sent!</div>
                <div className="text-body1Regular text-neutral-800">
                  A representative will be in touch.
                </div>
              </>
            )}
            {error && (
              <div className="text-h4Medium text-red-medium">
                Something went wrong.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-xl p-s ml:mx-auto ml:w-[368px]">
          <FormInput
            onChange={onChangeName}
            label="Name*"
            placeholder="Your name"
            value={name}
          />
          <FormInput
            onChange={onChangeEmail}
            label="Email address*"
            placeholder="example@email.com"
            value={email}
            invalid={!!email && !emailValid}
            errorMessage="Not a valid email address"
          />
          <FormInput
            onChange={onChangePhone}
            label="Phone number (optional)"
            placeholder="___-___-____"
            value={phone}
            invalid={!!phone && !phoneValid}
            errorMessage="Not a valid phone number"
          />
          <div className="s:px-s">
            <Button
              aria-label="Send savings confirmation"
              variant={ButtonVariants.Tertiary}
              onClick={onSubmit}
              disabled={!formValid}
            >
              Send
            </Button>
          </div>
        </div>
      )}
    </OverlayWrapper>
  );
};
